import React from 'react';
import { useTranslation } from 'react-i18next';
import type { JackpotComponent } from '../../../types/builder';
import { ColorPicker } from '../../Common/ColorPicker';
import { ImageUploader } from '../../Common/ImageUploader';
import { ToggleSwitch } from '../../Common/ToggleSwitch';

interface JackpotSettingsProps {
  component: JackpotComponent;
  onUpdate: (settings: JackpotComponent['settings']) => void;
}

const STYLE_OPTIONS = [
  { value: 'default', key: 'default' },
  { value: 'compact', key: 'compact' },
  { value: 'modern', key: 'modern' },
  { value: 'luxe', key: 'luxe' },
] as const;

export const JackpotSettings: React.FC<JackpotSettingsProps> = ({ component, onUpdate }) => {
  const { t } = useTranslation('builder');
  const { settings } = component;

  const handleChange = (field: string, value: any) => {
    onUpdate({
      ...settings,
      [field]: value,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.jackpot.displayStyle')}
        </label>
        <select
          value={settings.style || 'default'}
          onChange={(e) => handleChange('style', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        >
          {STYLE_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {t(`settings.jackpot.styles.${s.key}` as const)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.title')}
        </label>
        <input
          type="text"
          value={settings.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.jackpot.initialAmount')}
        </label>
        <input
          type="number"
          value={settings.amount}
          onChange={(e) => handleChange('amount', parseFloat(e.target.value))}
          step="0.01"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.jackpot.unit')}
        </label>
        <input
          type="text"
          value={settings.currency}
          onChange={(e) => handleChange('currency', e.target.value)}
          placeholder={t('settings.jackpot.unitPlaceholder')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.jackpot.incrementAmount')}
        </label>
        <input
          type="number"
          value={settings.incrementAmount}
          onChange={(e) => handleChange('incrementAmount', parseFloat(e.target.value))}
          step="0.01"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
        <p className="text-xs text-gray-500 mt-1">{t('settings.jackpot.incrementAmountHint')}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.jackpot.incrementSpeedMs')}
        </label>
        <input
          type="number"
          value={settings.incrementSpeed}
          onChange={(e) => handleChange('incrementSpeed', parseInt(e.target.value))}
          step="100"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
        <p className="text-xs text-gray-500 mt-1">{t('settings.jackpot.incrementSpeedHint')}</p>
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.jackpot.showIcon')}
          checked={settings.showIcon}
          onChange={(checked) => handleChange('showIcon', checked)}
        />
      </div>

      {settings.showIcon && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.jackpot.iconImage')}
          </label>
          <ImageUploader
            currentImage={settings.icon}
            onImageChange={(url) => handleChange('icon', url)}
            aspectRatio="1:1"
          />
          <p className="text-xs text-gray-500 mt-1">{t('settings.jackpot.iconImageHint')}</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.alignment')}
        </label>
        <select
          value={settings.alignment}
          onChange={(e) => handleChange('alignment', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        >
          <option value="left">{t('settings.common.alignLeft')}</option>
          <option value="center">{t('settings.common.alignCenter')}</option>
          <option value="right">{t('settings.common.alignRight')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.backgroundColor')}
        </label>
        <ColorPicker
          color={settings.backgroundColor}
          onChange={(color) => handleChange('backgroundColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.textColor')}
        </label>
        <ColorPicker
          color={settings.textColor}
          onChange={(color) => handleChange('textColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.jackpot.amountColor')}
        </label>
        <ColorPicker
          color={settings.amountColor}
          onChange={(color) => handleChange('amountColor', color)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.padding')}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={settings.padding}
          onChange={(e) => handleChange('padding', parseInt(e.target.value))}
          className="w-full"
        />
        <div className="text-sm text-gray-600 text-center mt-1">{settings.padding}px</div>
      </div>
    </div>
  );
};
